/**
 * Radial: the root in the middle, and everything else in rings around it.
 *
 * The shape a mind map has on a whiteboard. Every branch of the root is given a *wedge* of the
 * ring, sized by how many leaves it carries, and its whole subtree stays inside that wedge — so
 * two branches can never cross, whatever either of them grows into. Depth is distance from the
 * centre: every node at depth `d` sits on ring `d`.
 *
 * The rings are ovals, `ASPECT` times wider than they are tall, and that is the whole of what
 * keeps a radial map readable. A label is wide and short. On a circle, the nodes at the top and
 * bottom lie with their width along the ring, so the ring has to be big enough for the widest of
 * them — and it is then that big at the sides too, where the same labels need only their height.
 * A twelve-leaf map of ordinary labels came out as tall as it was wide and, fitted to a screen or
 * a card, a map of specks. An oval spends the room where labels need it: along the top and the
 * bottom, where they sit side by side.
 *
 * Two constraints decide how big each ring is, and the ring takes whichever is larger:
 *
 * - **Clearance from the ring inside it**, so that a node is never on top of the one it hangs
 *   from, at whatever angle the two of them sit.
 * - **Room along the ring.** A node's wedge is a stretch of the ring; the stretch has to be longer
 *   than the node is across, measured along the ring, or it overlaps its neighbour. A crowded ring
 *   moves outwards until every node fits in its own slice.
 *
 * Like the trees: the root does not move, collapsed branches take no room, a pinned node keeps
 * its place, and a second run over an arranged map reports nothing.
 */
import { childrenOf } from '@orbit-mind/domain';
import { onlyMoves } from './moves';
import { spacingFrom, } from './types';
export const RADIAL_LAYOUT_ID = 'radial';
/** Where the first branch starts: straight up, then clockwise, which is how a clock is read. */
const START_ANGLE = -Math.PI / 2;
/**
 * How much wider than tall every ring is.
 *
 * About the proportion of a screen, which is also about the proportion that labels of a few words
 * pack best in. Further than this and the map stops reading as rings at all.
 */
const ASPECT = 1.6;
/**
 * Slack on the room constraint.
 *
 * The constraint is written for the stretch of ring between two nodes, and what keeps them apart
 * is the chord, which is shorter. The difference is a few percent at the angles that are ever
 * tight.
 */
const ARC_SLACK = 1.15;
/** The middle of a node's wedge, which is where it sits. */
function bisector(slot) {
    return (slot.from + slot.to) / 2;
}
/**
 * How far along a ring of size 1 a step of one radian goes, at angle `t`.
 *
 * `ASPECT` at the top and bottom, where the oval is long, and 1 at the sides, where it is short.
 */
function stride(t) {
    return Math.hypot(ASPECT * Math.sin(t), Math.cos(t));
}
/**
 * How much of a box lies along the ring at angle `t`: its shadow on the ring's tangent there.
 *
 * The width, at the top of the ring, and the height at the sides — never more than the diagonal,
 * and for a wide label, almost everywhere, much less.
 */
function acrossRing(size, t) {
    const along = stride(t);
    return (Math.abs((size.width * ASPECT * Math.sin(t)) / along) +
        Math.abs((size.height * Math.cos(t)) / along));
}
export function packRadial(input) {
    const { document } = input;
    const root = document.nodes[document.map.rootNodeId];
    if (root === undefined)
        return { positions: {} };
    const spacing = spacingFrom(input.spacing);
    const pinned = (node) => node.pinned === true && input.preserveManualPositions;
    const children = (node) => node.collapsed ? [] : childrenOf(document, node.id).filter((child) => !child.hidden);
    // Leaves under each node: the share of the ring a branch is owed.
    const leaves = new Map();
    const leafCount = (node) => {
        const known = leaves.get(node.id);
        if (known !== undefined)
            return known;
        const below = children(node);
        const count = below.length === 0 ? 1 : below.reduce((sum, child) => sum + leafCount(child), 0);
        leaves.set(node.id, count);
        return count;
    };
    // Every node's wedge, breadth first so the rings come out in order.
    const rings = [];
    const queue = [
        { node: root, depth: 0, from: START_ANGLE, to: START_ANGLE + 2 * Math.PI },
    ];
    while (queue.length > 0) {
        const slot = queue.shift();
        if (slot === undefined)
            break;
        if (slot.depth > 0) {
            const ring = rings[slot.depth] ?? [];
            ring.push(slot);
            rings[slot.depth] = ring;
        }
        const below = children(slot.node);
        const total = below.reduce((sum, child) => sum + leafCount(child), 0);
        let cursor = slot.from;
        for (const child of below) {
            const share = ((slot.to - slot.from) * leafCount(child)) / total;
            queue.push({ node: child, depth: slot.depth + 1, from: cursor, to: cursor + share });
            cursor += share;
        }
    }
    /*
     * Each ring's size: its height from the centre, the width being `ASPECT` times that.
     *
     * The clearance is worked out for a node and the one inside it at the same angle, which is
     * where a child sits relative to its parent. Going out from one ring to the next moves a box
     * `ASPECT` times further sideways than downwards, and the two boxes are clear of each other
     * when either movement alone is enough — sideways by their half-widths, or downwards by their
     * half-heights, with `rank` between. The worst angle is the one where both fall just short, and
     * the step that clears it is the length of the two requirements taken together.
     */
    const scales = [0];
    let inner = input.extentOf(root.id);
    for (let depth = 1; depth < rings.length; depth += 1) {
        const ring = rings[depth] ?? [];
        let widest = 0;
        let tallest = 0;
        let room = 0;
        for (const slot of ring) {
            const size = input.extentOf(slot.node.id);
            widest = Math.max(widest, size.width);
            tallest = Math.max(tallest, size.height);
            const t = bisector(slot);
            // An only child can be owed the whole ring; half of it is already more than any box.
            const arc = Math.min(slot.to - slot.from, Math.PI);
            room = Math.max(room, ((acrossRing(size, t) + spacing.sibling) * ARC_SLACK) / (arc * stride(t)));
        }
        const sideways = (inner.width + widest) / 2 + spacing.rank;
        const downwards = (inner.height + tallest) / 2 + spacing.rank;
        const step = Math.hypot(sideways / ASPECT, downwards);
        scales[depth] = Math.max(room, (scales[depth - 1] ?? 0) + step);
        inner = { width: widest, height: tallest };
    }
    const rootSize = input.extentOf(root.id);
    const centre = { x: root.x + rootSize.width / 2, y: root.y + rootSize.height / 2 };
    const placements = {};
    for (let depth = 1; depth < rings.length; depth += 1) {
        const scale = scales[depth] ?? 0;
        for (const slot of rings[depth] ?? []) {
            if (pinned(slot.node))
                continue;
            const t = bisector(slot);
            const size = input.extentOf(slot.node.id);
            placements[slot.node.id] = {
                x: centre.x + ASPECT * scale * Math.cos(t) - size.width / 2,
                y: centre.y + scale * Math.sin(t) - size.height / 2,
            };
        }
    }
    return { positions: onlyMoves(document, placements) };
}
export const radialLayout = {
    id: RADIAL_LAYOUT_ID,
    name: 'Radial',
    run: packRadial,
};
//# sourceMappingURL=radial.js.map